import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import DescriptionIcon from '@mui/icons-material/Description';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PeopleIcon from '@mui/icons-material/People';
import ListAltIcon from '@mui/icons-material/ListAlt';
import logo from '../assets/geoid-logo.png';
import { useAuth } from '../utils/auth';
import API_BASE_URL from '../config';

// charts and maps
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import { Treemap } from 'recharts';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, LayerGroup, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// fix default icon path for leaflet in CRA / webpack contexts
delete L.Icon.Default.prototype._getIconUrl;
try {
  const iconRetina = require('leaflet/dist/images/marker-icon-2x.png');
  const icon = require('leaflet/dist/images/marker-icon.png');
  const shadow = require('leaflet/dist/images/marker-shadow.png');
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: iconRetina,
    iconUrl: icon,
    shadowUrl: shadow,
  });
} catch (e) {
  // if require fails in some bundlers, ignore and let leaflet try its defaults
}

const drawerWidth = 240;

export default function DashboardLayout() {
  const { logout, user } = useAuth();
  const storedName = localStorage.getItem('name');
  const [evidences, setEvidences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // fetch evidences for dashboard
  useEffect(() => {
    let mounted = true;
    const fetchEvidences = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/evidences/dashboard`, {
          headers: {
            'Accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error('Failed to fetch evidences');
        const data = await res.json();
        const items = Array.isArray(data) ? data : (data.evidences || data.items || []);
        if (mounted) setEvidences(items);
      } catch (e) {
        if (mounted) setError(e.message || 'Unknown error');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchEvidences();
    return () => { mounted = false; };
  }, []);

  const sidebarItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    // { label: 'Live Monitoring', icon: <LiveTvIcon />, path: '/live-monitoring' },
    { label: 'Evidences', icon: <DescriptionIcon />, path: '/evidences' },
    { label: 'Vehicles', icon: <DirectionsCarIcon />, path: '/vehicles' },
    { label: 'Drivers', icon: <PeopleIcon />, path: '/drivers' },
    { label: 'Log', icon: <ListAltIcon />, path: '/log' },
  ];

  const drawer = (
    <div>
      <Toolbar sx={{ justifyContent: 'center', bgcolor: 'black', minHeight: 80 }}>
        <img src={logo} alt="DVR Management System" style={{ maxWidth: 160, maxHeight: 48, margin: '0 auto', display: 'block', background: 'black', padding: 8, borderRadius: 8 }} />
      </Toolbar>
      <Divider />
      <List>
        {sidebarItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton onClick={() => navigate(item.path)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Welcome, {storedName || user?.name || 'User'}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, overflowX: 'hidden' }}
      >
        <Toolbar />
        {useLocation().pathname === '/' ? (
          <DashboardPage
            evidences={evidences}
            loading={loading}
            error={error}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
          />
        ) : (
          <Outlet />
        )}
      </Box>
    </Box>
  );
}

function DashboardPage({ evidences, loading, error, fromDate, toDate, setFromDate, setToDate }) {
  // parse and filter evidences by date range
  const parsed = useMemo(() => (evidences || []).map(it => ({
    ...it,
    AlertDateTime: it.AlertDateTime || it.created_at || it.createdAt,
  lat: (() => { const v = parseFloat(it.Latitude ?? it.latitude ?? it.Lat ?? it.lat ?? it.Latitude); return Number.isFinite(v) ? v : NaN })(),
  lng: (() => { const v = parseFloat(it.Longnitude ?? it.Longitude ?? it.Long ?? it.lng ?? it.Longnitude); return Number.isFinite(v) ? v : NaN })(),
  })), [evidences]);

  const filtered = useMemo(() => {
    if (!fromDate && !toDate) return parsed;
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    return parsed.filter(p => {
      const d = p.AlertDateTime ? new Date(p.AlertDateTime) : null;
      if (!d) return false;
      if (from && d < from) return false;
      if (to) {
        // include whole day for toDate
        const toEnd = new Date(to);
        toEnd.setHours(23,59,59,999);
        if (d > toEnd) return false;
      }
      return true;
    });
  }, [parsed, fromDate, toDate]);

  const total = filtered.length;

  // markers: only include finite lat/lng
  const markers = useMemo(() => {
    const list = filtered.map((it) => ({
      lat: it.lat,
      lng: it.lng,
      driver: it.driver_name || it.DriverID || it.driver || 'Driver',
      vehicle: it.VehicleId || it.Vehicle || it.Vehicle_Id || '',
      when: it.AlertDateTime,
      alert: it.alert_name,
      original: it,
    })).filter(m => Number.isFinite(m.lat) && Number.isFinite(m.lng));
    // debug a few entries when developing
    if (process.env.NODE_ENV !== 'production') console.debug('Dashboard markers sample', list.slice(0,5));
    return list;
  }, [filtered]);

  const byDriver = useMemo(() => {
    const map = {};
    filtered.forEach(it => {
      const driver = it.driver_name || it.driver || it.DriverID || 'Unknown';
      map[driver] = (map[driver] || 0) + 1;
    });
    return Object.entries(map).map(([driver, count]) => ({ driver, count })).sort((a,b) => b.count - a.count);
  }, [filtered]);

  // Evidence type counts
  const byAlert = useMemo(() => {
    const map = {};
    filtered.forEach(it => { const a = it.alert_name || it.alert || 'Unknown'; map[a] = (map[a] || 0) + 1; });
    return Object.entries(map).map(([alert, count]) => ({ alert, count })).sort((a,b) => b.count - a.count);
  }, [filtered]);

  // predefined alert list for tiles (order similar to screenshot)
  const ALERT_TILE_LIST = [
    'Phone Detection',
    'Geo-Fencing Alarm',
    'Yawning',
    'High Speed',
    'Smoking',
    'Illegal Shutdown',
    'Driver Fatigue',
    'Acceleration Alarm',
    'Lane Departure'
  ];

  const alertTiles = useMemo(() => {
    const counts = {};
    // initialize
    ALERT_TILE_LIST.forEach(a => counts[a] = 0);
    // map some incoming alert names to tiles
    const mapping = {
      'Phone Detection': 'Phone Detection',
      'Yawning': 'Yawning',
      'H-speed': 'High Speed',
      'High-speed alarm': 'High Speed',
      'High Speed': 'High Speed',
      'Smoking': 'Smoking',
      'Illegal Shutdown': 'Illegal Shutdown',
      'Driver Fatigue': 'Driver Fatigue',
      'Acceleration Alarm': 'Acceleration Alarm',
      'Lane Departure': 'Lane Departure',
      'Distract': 'Phone Detection',
      'Fatigue': 'Driver Fatigue',
      'Geo-Fencing Alarm': 'Geo-Fencing Alarm',
    };
    filtered.forEach(it => {
      const a = it.alert_name || it.alert || 'Unknown';
      const key = mapping[a] || a;
      if (!counts[key]) counts[key] = 0;
      counts[key] += 1;
    });
    return ALERT_TILE_LIST.map(k => ({ label: k, count: counts[k] || 0 }));
  }, [filtered]);

  // monthly totals for area chart (by month-year)
  const monthly = useMemo(() => {
    const map = {};
    filtered.forEach(it => {
      const d = it.AlertDateTime ? new Date(it.AlertDateTime) : null;
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).map(([k,v]) => ({ month: k, count: v })).sort((a,b) => a.month.localeCompare(b.month));
  }, [filtered]);

  // group-wise (company) pie data
  const byCompany = useMemo(() => {
    const map = {};
    filtered.forEach(it => { const c = it.Company_Name || it.company || 'Unknown'; map[c] = (map[c] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a,b) => b.value - a.value).slice(0,12);
  }, [filtered]);

  // legend payload for pie chart (use same colors as below)
  const companyLegend = useMemo(() => {
    const colors = ["#3f51b5","#2196f3","#4caf50","#ff9800","#9c27b0"];
    return byCompany.map((c, i) => ({ id: c.name, value: c.name, color: colors[i % colors.length] }));
  }, [byCompany]);

  // vehicle treemap data
  const treemapData = useMemo(() => {
    const map = {};
    filtered.forEach(it => { const v = it.VehicleId || it.Vehicle || it.Vehicle_Id || 'Unknown'; map[v] = (map[v] || 0) + 1; });
    return Object.entries(map).map(([name, size]) => ({ name, size })).sort((a,b) => b.size - a.size).slice(0,50);
  }, [filtered]);

  // markers grouped by rounded lat/lng to emulate heat density, collect vehicles and evidence names
  const heatGroups = useMemo(() => {
    const m = {};
    markers.forEach(it => {
      const lat = Number(it.lat.toFixed(3));
      const lng = Number(it.lng.toFixed(3));
      const k = `${lat},${lng}`;
      if (!m[k]) {
        m[k] = { lat, lng, count: 0, vehicles: new Set(), evidences: new Set() };
      }
      m[k].count += 1;
      if (it.vehicle) m[k].vehicles.add(it.vehicle);
      if (it.alert) m[k].evidences.add(it.alert);
    });
    // Convert sets to arrays for rendering
    return Object.values(m).map(g => ({ ...g, vehicles: Array.from(g.vehicles), evidences: Array.from(g.evidences) }));
  }, [markers]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ width: { xs: '100%', sm: 'auto' }, bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', boxSizing: 'border-box' }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Total Evidences of last 24hrs</Typography>
          <Typography variant="h4">{total.toLocaleString()}</Typography>
        </Box>
        {alertTiles.map(tile => (
          <Box key={tile.label} sx={{ width: { xs: 'calc(50% - 8px)', sm: 120 }, bgcolor: 'background.paper', p: 1.5, borderRadius: 1, boxShadow: 1, boxSizing: 'border-box' }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>{tile.label}</Typography>
            <Typography variant="h6">{tile.count.toLocaleString()}</Typography>
          </Box>
        ))}
      </Box>

      {error && <Typography color="error">{error}</Typography>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300, maxWidth: '100%', height: 300, bgcolor: 'background.paper', p: 1, overflow: 'hidden' }}>
          <Typography variant="h6">Evidence Type by Alarms</Typography>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byAlert.slice(0,10).map(d => ({ name: d.alert, value: d.count }))} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => [value, 'Evidences']} />
              <Bar dataKey="value" fill="#0b74de" />
            </BarChart>
          </ResponsiveContainer>
        </Box>

  <Box sx={{ flex: 1, minWidth: 300, maxWidth: '100%', height: 300, bgcolor: 'background.paper', p: 1, overflow: 'hidden' }}>
          <Typography variant="h6">Group wise evidence</Typography>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={byCompany} dataKey="value" nameKey="name" outerRadius={80} innerRadius={40} label={false}>
                {byCompany.map((_, i) => <Cell key={i} fill={["#3f51b5","#2196f3","#4caf50","#ff9800","#9c27b0"][i%5]} />)}
              </Pie>
              <Tooltip formatter={(value) => [value, 'Evidences']} />
              <Legend payload={companyLegend} layout="horizontal" verticalAlign="bottom" align="center" />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
        <Box sx={{ flex: 1, minWidth: 320, maxWidth: '100%', height: 360, bgcolor: 'background.paper', p: 1, overflow: 'hidden' }}>
          <Typography variant="h6">Evidence Map</Typography>
          <Box sx={{ height: 300 }}>
            <MapContainer center={[6.9271,79.8612]} zoom={7} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {heatGroups.map((g, i) => (
                <CircleMarker key={i} center={[g.lat, g.lng]} radius={Math.min(20, 4 + Math.log(g.count + 1) * 4)} color={g.count > 5 ? 'red' : g.count > 2 ? 'orange' : 'blue'} opacity={0.6} fillOpacity={0.4}>
                  <Popup>
                    <div>
                      <strong>{g.count} events</strong><br />
                      <span>Lat/Lng: {g.lat.toFixed(3)}, {g.lng.toFixed(3)}</span><br />
                      <span><strong>Vehicles:</strong> {g.vehicles.length ? g.vehicles.join(', ') : 'N/A'}</span><br />
                      <span><strong>Evidences:</strong> {g.evidences.length ? g.evidences.join(', ') : 'N/A'}</span>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 320, maxWidth: '100%', height: 360, bgcolor: 'background.paper', p: 1, overflow: 'hidden' }}>
          <Typography variant="h6">Vehicle Evidence Treemap</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <Treemap
              data={treemapData}
              dataKey="size"
              ratio={4/3}
              stroke="#fff"
              fill="#1976d2"
              content={({ x, y, width, height, name, size }) => (
                <g>
                  <rect x={x} y={y} width={width} height={height} style={{ fill: '#1976d2', stroke: '#fff' }} />
                  {width > 40 && height > 20 && (
                    <text x={x + 4} y={y + 16} fontSize={12} fill="#fff">{name}</text>
                  )}
                  {width > 40 && height > 36 && (
                    <text x={x + 4} y={y + 32} fontSize={11} fill="#fff">{size}</text>
                  )}
                </g>
              )}
            />
          </ResponsiveContainer>
        </Box>

        <Box sx={{ flex: 1, minWidth: 320, maxWidth: '100%', height: 360, bgcolor: 'background.paper', p: 1, overflow: 'hidden' }}>
          <Typography variant="h6">Driver wise Evidence Count</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byDriver.slice(0,40).map(d => ({ name: d.driver, value: d.count }))} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={60} />
              <YAxis />
              <Tooltip formatter={(value) => [value, 'Evidences']} />
              <Bar dataKey="value" fill="#388e3c" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
}
